import type { DocEntityType } from "building-types/doc/docEntityTypes";
import type { UserNotificationType } from "common/types/userNotification";

/**
 * This error class is intended to be used within the context of
 * editorApplyDocAction. If desired, you can use this as a means
 * to throw an error from anywhere in the callchain of a
 * doc operation or series of operations, resulting in a UserNotificationType
 * being dispatched to the user in the UI.
 *
 * Extending classes below are intended to reduce repetition of similar error
 * messages used in multiple contexts.
 *
 * Note: the code responsible for catching these errors and dispatching the
 * notification is defined within the scope of editorApplyDocAction.
 */

export class DocEntityTransactErrorNotification extends Error {
  public entityType?: DocEntityType;

  constructor(message: string, entityType?: DocEntityType) {
    super(message);
    this.entityType = entityType;
  }

  log() {
    console.error(`DocEntityError${this.entityType ? ` for ${this.entityType}` : " "} -> ${this.message}`);
  }

  get userNotification(): UserNotificationType {
    return {
      message: this.message,
      messageType: "error",
      timeout: 6000,
    };
  }
}

export function isDocEntityTransactErrorNotification(error: unknown): error is DocEntityTransactErrorNotification {
  return error instanceof DocEntityTransactErrorNotification;
}

export class ReportableUnitDesignatorLabelErrorNotification extends DocEntityTransactErrorNotification {
  constructor() {
    super(
      "Cannot add reportable unit designator label to an entity that is a member of a component.",
      "REPORTABLE_UNIT_DESIGNATION",
    );
  }
}

export class MultipleReportableUnitLabelsErrorNotification extends DocEntityTransactErrorNotification {
  constructor() {
    super(
      "An option set can only carry one reportable unit designator label. Remove the existing one first.",
      "REPORTABLE_UNIT_DESIGNATION",
    );
  }
}

export class ComponentCircularDependencyErrorNotification extends DocEntityTransactErrorNotification {
  constructor(componentId: string, stack?: string[]) {
    super("Circular dependency detected. A component is nested inside itself.", "COMPONENT_INSTANCE");
    console.error(`Component circular dependency detected: ${stack?.join(" -> ")} -> ${componentId}`);
  }
}
